import React, {useState} from 'react';

import {useDeleteFolder} from 'app/folders/api';
import {EditFolder} from 'app/folders/components/dashboards/EditFolder';
import {useMoveModal} from 'app/folders/components/MoveFolderModal';
import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {useDeleteModal} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {getDocumentsQRCodeURL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import TabbedModal from './TabbedModal';

import type {BasicTeamSerializedType, FolderSerializedType} from 'sweet-types';

interface Props {
  object: FolderSerializedType;
  refreshData: () => Promise<unknown>;
  allowedTeams: Array<BasicTeamSerializedType>;
}

export function FolderActionsButton({
  object,
  refreshData,
  allowedTeams,
}: Props) {
  const setDeleting = useDeleteModal();
  const {show: showMoveModal, Modal: MoveModal} = useMoveModal();
  const [deleteFolder] = useDeleteFolder();
  const [editFolder, setEditFolder] = useState<boolean>(false);

  return (
    <div className="row d-flex align-items-center justify-content-between position-relative">
      <div className="col-auto order-lg-5">
        {object.can_edit ? (
          <ActionsButton
            actions={[
              {
                icon: 'fa fa-edit',
                text: gt('Edit'),
                callback: () => setEditFolder(true),
                key: 'fa fa-edit',
              },
              {
                icon: 'fa fa-arrows-alt',
                text: gt('Move'),
                callback: () =>
                  showMoveModal({
                    object,
                    action: () => Promise.resolve(true),
                    callback: refreshData,
                    help: gt(
                      'When you move this folder, all its contents will move with it. It will adopt the teams of the folder you place it into.'
                    ),
                  }),
                key: 'fa fa-arrows-alt',
              },
              {
                text: <a>{gt('Show QR Codes')}</a>,
                icon: 'fa fa-qrcode',
                key: 'qrcodes',
                condition: () => true,
                callback: () =>
                  window.open(getDocumentsQRCodeURL(object.hashid)),
              },
              {
                text: gt('Delete'),
                icon: 'far fa-trash',
                key: 'remove-folder',
                callback: () => {
                  setDeleting({
                    object,
                    action: deleteFolder,
                    callback: refreshData,
                    help: gt(
                      'This folder and all sub folders and documents will be deleted.'
                    ),
                  });
                },
              },
            ]}
          />
        ) : null}
      </div>
      <EditFolderModal
        show={editFolder}
        close={() => setEditFolder(false)}
        folder={object}
        allowedTeams={allowedTeams}
        refreshData={refreshData}
      />
      {MoveModal}
    </div>
  );
}

interface EditFolderProps {
  show: boolean;
  close: () => void;
  folder: FolderSerializedType;
  allowedTeams: Array<BasicTeamSerializedType>;
  refreshData: () => Promise<unknown>;
}

function EditFolderModal({
  show,
  close,
  folder,
  allowedTeams,
  refreshData,
}: EditFolderProps) {
  return (
    <TabbedModal
      show={show}
      onCancel={close}
      tabs={[
        {
          eventKey: 'edit-folder',
          name: gt('Edit Folder'),
          icon: 'far fa-folder',
          content: (
            <EditFolder
              item={folder}
              parent={{teams: allowedTeams}}
              onClose={() => {
                refreshData().then(close);
              }}
            />
          ),
        },
      ]}
    />
  );
}
